import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
// ✅ Async Thunks
// 0. Fetch Active Users
export const fetchActiveUsersThunk = createAsyncThunk("opportunity/fetchActiveUsers", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/users?status=active&limit=100", "", { Authorization: token || "" });
        if (response && response.data?.success) {
            return response.data.data.users;
        }
        else {
            return rejectWithValue("Failed to fetch users");
        }
    }
    catch (error) {
        return rejectWithValue(error.message || "An error occurred");
    }
});
import { getCache, setCache, generateCacheKey, clearCache } from "../helper/cache";
// 1. Dropdown Options
export const fetchDropdownOptions = createAsyncThunk("opportunity/fetchDropdownOptions", async (payload = {}, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const moduleKey = getState().userData?.activeModuleKey || "";
        const cacheKey = generateCacheKey("dropdown", { moduleKey, ...payload });
        const cached = getCache(cacheKey);
        if (cached) {
            return cached;
        }
        const response = await ApiCall("GET", "/api/dropdown-options", payload, { Authorization: token || "" });
        if (response && response.data?.success) {
            setCache(cacheKey, response.data, 3600); // Cache for 1 hour
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Failed to fetch dropdown options");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Failed to fetch dropdown options");
    }
});
// 2. Opportunity Directory Validation
export const checkBulkUploadThunk = createAsyncThunk("opportunity/checkBulkUpload", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/opportunity-directory/bulk-upload?validateOnly=true", payload, { Authorization: token || "" });
        // ApiCall returns the error object instead of throwing it
        const isError = response instanceof Error || (response && response.isAxiosError);
        const responseData = isError ? response?.response?.data : response?.data;
        if (responseData && responseData.success) {
            return responseData;
        }
        else {
            return rejectWithValue(responseData || response?.message || "Validation failed");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data ||
            error?.message ||
            "Validation failed");
    }
});
// 3. Update Opportunity
export const updateOpportunityThunk = createAsyncThunk("opportunity/update", async ({ id, data }, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", `/api/opportunity-directory/update/${id}`, data, { Authorization: token || "" });
        if (response && response.data?.success) {
            clearCache("opportunities");
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Update failed");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Update failed");
    }
});
// 4. Opportunity Directory Bulk Upload
export const bulkUploadOpportunitiesThunk = createAsyncThunk("opportunity/bulkUpload", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/opportunity-directory/bulk-upload", payload, { Authorization: token || "" });
        if (response && response.data?.success) {
            clearCache("opportunities");
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Failed to bulk upload opportunities");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Bulk upload failed");
    }
});
// 3. Create Opportunity
export const createOpportunityThunk = createAsyncThunk("opportunity/create", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/opportunity-directory/create", payload, { Authorization: token || "" });
        if (response && response.data?.success) {
            clearCache("opportunities");
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Failed to create opportunity");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Failed to create opportunity");
    }
});
// 4. Get Opportunity Directory
// Sentinel used when a fetch is aborted by navigation — the rejected reducer
// treats it as a no-op (keeps existing data, shows no error toast).
const FETCH_CANCELED = "__FETCH_CANCELED__";
export const fetchOpportunitiesThunk = createAsyncThunk("opportunity/fetchAll", async (payload = {}, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const moduleKey = getState().userData?.activeModuleKey || "";
        const response = await ApiCall("GET", "/api/opportunity-directory", payload, { Authorization: token || "" });
        // Request was aborted by a route change (abortAllApiCalls). This is not a
        // real failure — bail quietly so we don't show an error or wipe the list.
        if (response?.code === "ERR_CANCELED" || response?.name === "CanceledError") {
            return rejectWithValue(FETCH_CANCELED);
        }
        if (response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Failed to fetch opportunities");
        }
    }
    catch (error) {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
            return rejectWithValue(FETCH_CANCELED);
        }
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Failed to fetch opportunities");
    }
});
// 5. Delete Opportunity
export const deleteOpportunityThunk = createAsyncThunk("opportunity/delete", async (id, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("DELETE", `/api/opportunity-directory/delete/${id}`, "", { Authorization: token || "" });
        if (response && response.data?.success) {
            clearCache("opportunities");
            return response.data;
        }
        else {
            return rejectWithValue(response?.data?.message || "Delete failed");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Delete failed");
    }
});
// 6. Fetch Preferences
export const fetchPreferencesThunk = createAsyncThunk("opportunity/fetchPreferences", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/api/preferences", null, { Authorization: token || "" });
        if (response && response.data?.success) {
            return response.data.data || {};
        }
        else {
            return rejectWithValue("Failed to fetch preferences");
        }
    }
    catch (error) {
        return rejectWithValue(error.message || "An error occurred");
    }
});
// 7. Save Preferences
export const savePreferencesThunk = createAsyncThunk("opportunity/savePreferences", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/preferences", payload, { Authorization: token || "" });
        if (response && response.data?.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue("Failed to save preferences");
        }
    }
    catch (error) {
        return rejectWithValue(error.message || "An error occurred");
    }
});
// ✅ Initial State
const initialState = {
    loading: false,
    listLoading: false,
    success: false,
    error: null,
    dropdownOptions: null,
    opportunities: null,
    users: [],
    tags: [],
    total: 0,
    page: 1,
    limit: 20,
    lastUpdated: null,
    // ✅ Preferences initial state
    preferences: null,
    preferencesLoading: false,
    preferencesError: null,
    preferencesSaving: false,
    preferencesSaveError: null,
};
// ✅ Slice
const opportunitySlice = createSlice({
    name: "opportunity",
    initialState,
    reducers: {
        clearOpportunityState: (state) => {
            state.loading = false;
            state.success = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Active Users
            .addCase(fetchActiveUsersThunk.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchActiveUsersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.users = action.payload;
        })
            .addCase(fetchActiveUsersThunk.rejected, (state) => {
            state.loading = false;
        })
            // Fetch Dropdown Options
            .addCase(fetchDropdownOptions.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchDropdownOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.dropdownOptions = action.payload.data;
        })
            .addCase(fetchDropdownOptions.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch dropdown options";
            if (state.error)
                toast.error(state.error);
        })
            // Bulk Upload
            .addCase(bulkUploadOpportunitiesThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = false;
        })
            .addCase(bulkUploadOpportunitiesThunk.fulfilled, (state) => {
            state.loading = false;
            state.success = true;
            // toast.success("Bulk upload successful");
        })
            .addCase(bulkUploadOpportunitiesThunk.rejected, (state, action) => {
            state.loading = false;
            state.success = false;
            state.error = action.payload || "Bulk upload failed";
            if (state.error)
                toast.error(state.error);
        })
            // Create Opportunity
            .addCase(createOpportunityThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = false;
        })
            .addCase(createOpportunityThunk.fulfilled, (state) => {
            state.loading = false;
            state.success = true;
            // toast.success("Opportunity created successfully");
        })
            .addCase(createOpportunityThunk.rejected, (state, action) => {
            state.loading = false;
            state.success = false;
            state.error = action.payload || "Failed to create opportunity";
            if (state.error)
                toast.error(state.error);
        })
            // Fetch Opportunities
            .addCase(fetchOpportunitiesThunk.pending, (state) => {
            state.listLoading = true;
            state.error = null;
        })
            .addCase(fetchOpportunitiesThunk.fulfilled, (state, action) => {
            state.listLoading = false;
            // ✅ Corrected to match backend returnResponse structure
            state.opportunities = action.payload.data?.result?.data || [];
            state.tags = action.payload.data?.result?.tags || [];
            state.lastUpdated = action.payload.data?.result?.lastUpdated || null;
            state.total = action.payload.data?.total || 0;
            state.page = action.payload.data?.page || 1;
            state.limit = action.payload.data?.limit || 20;
        })
            .addCase(fetchOpportunitiesThunk.rejected, (state, action) => {
            state.listLoading = false;
            // Aborted by navigation — keep existing data and don't surface an error.
            if (action.payload === FETCH_CANCELED)
                return;
            state.opportunities = [];
            state.error = action.payload || "Failed to fetch opportunities";
            if (state.error)
                toast.error(state.error);
        })
            // Delete Opportunity
            .addCase(deleteOpportunityThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.success = false;
        })
            .addCase(deleteOpportunityThunk.fulfilled, (state) => {
            state.loading = false;
            state.success = true;
        })
            .addCase(deleteOpportunityThunk.rejected, (state, action) => {
            state.loading = false;
            state.success = false;
            state.error = action.payload || "Delete failed";
            if (state.error)
                toast.error(state.error);
        })
            // Fetch Preferences
            .addCase(fetchPreferencesThunk.pending, (state) => {
            state.preferencesLoading = true;
            state.preferencesError = null;
        })
            .addCase(fetchPreferencesThunk.fulfilled, (state, action) => {
            state.preferencesLoading = false;
            state.preferences = action.payload;
        })
            .addCase(fetchPreferencesThunk.rejected, (state, action) => {
            state.preferencesLoading = false;
            state.preferencesError = action.payload || "Failed to fetch preferences";
        })
            // Save Preferences
            .addCase(savePreferencesThunk.pending, (state) => {
            state.preferencesSaving = true;
            state.preferencesSaveError = null;
        })
            .addCase(savePreferencesThunk.fulfilled, (state, action) => {
            state.preferencesSaving = false;
            state.preferences = action.payload.preferences || state.preferences;
        })
            .addCase(savePreferencesThunk.rejected, (state, action) => {
            state.preferencesSaving = false;
            state.preferencesSaveError = action.payload || "Failed to save preferences";
        });
    },
});
// ✅ Actions
export const { clearOpportunityState } = opportunitySlice.actions;
// ✅ Selectors
export const selectOpportunityLoading = (state) => state.opportunity.listLoading;
export const selectOpportunitySuccess = (state) => state.opportunity.success;
export const selectOpportunityError = (state) => state.opportunity.error;
export const selectDropdownOptions = (state) => state.opportunity.dropdownOptions;
export const selectOpportunities = (state) => state.opportunity.opportunities;
export const selectOpportunityTags = (state) => state.opportunity.tags;
export const selectOpportunityTotal = (state) => state.opportunity.total;
export const selectOpportunityPage = (state) => state.opportunity.page;
export const selectOpportunityLimit = (state) => state.opportunity.limit;
export const selectLastUpdated = (state) => state.opportunity.lastUpdated; // ✅ Added
export const selectPreferences = (state) => state.opportunity.preferences;
export const selectPreferencesLoading = (state) => state.opportunity.preferencesLoading;
// ✅ Reducer
export default opportunitySlice.reducer;
